/**
 * Sales & invoicing (Phase 4): quotations → sales orders → delivery notes → tax invoices, credit notes,
 * commission, VAT return and customer 360. Totals are always calculated on the server.
 */
import { z } from 'zod';
import { round2 } from './accounting';
import { round4 } from './inventory';
import { always } from './schemas';
import { VAT_SCENARIOS, type VatScenario } from './masters';

const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const req = (label: string, max: number) => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const optDate = z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null);
const uuid = (msg: string) => z.string({ error: msg }).uuid(msg);
const uuidOpt = z.union([z.literal(''), z.string().uuid()]).nullable().optional().transform((v) => v || null);
const qty = (label = 'Quantity') =>
  z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).max(1e9, `${label} is too large`)).refine((n) => n > 0, `${label} must be above 0`);
const rate = (label = 'Rate') => z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(1e9));

/** Prices exclusive of VAT (VAT added), inclusive (VAT inside the price) or outside the scope of VAT. */
export const VAT_MODES = ['EXCLUSIVE', 'INCLUSIVE', 'NONE'] as const;
export type VatMode = (typeof VAT_MODES)[number];
export const VAT_MODE_LABELS: Record<VatMode, string> = { EXCLUSIVE: 'Prices exclude VAT', INCLUSIVE: 'Prices include VAT', NONE: 'No VAT' };

/** Net, VAT and total of a sales line (discount first; VAT rounded to the fil per line). */
export function salesLineAmounts(q: number, r: number, discountPct: number, taxRate: number, mode: VatMode) {
  const gross = round2(q * r);
  const afterDiscount = round2(gross - round2((gross * (discountPct || 0)) / 100));
  if (mode === 'NONE' || !taxRate) return { net: afterDiscount, tax: 0, total: afterDiscount };
  if (mode === 'INCLUSIVE') {
    const net = round2(afterDiscount / (1 + taxRate / 100));
    return { net, tax: round2(afterDiscount - net), total: afterDiscount };
  }
  const tax = round2((afterDiscount * taxRate) / 100);
  return { net: afterDiscount, tax, total: round2(afterDiscount + tax) };
}

/** Where an order line is made: furniture planning, mattress production, or not made (stock / bought-in, services). */
export const PRODUCTION_PARTS = ['FURNITURE', 'MATTRESS', 'STOCK', 'SERVICE'] as const;
export type ProductionPart = (typeof PRODUCTION_PARTS)[number];
export const PRODUCTION_PART_LABELS: Record<ProductionPart, string> = { FURNITURE: 'Furniture (planning)', MATTRESS: 'Mattresses (mattress production)', STOCK: 'From stock / bought-in', SERVICE: 'Services' };

export const PRODUCTION_STATUSES = ['NONE', 'SENT', 'PLANNED', 'IN_PROGRESS', 'COMPLETED'] as const;
export type ProductionStatus = (typeof PRODUCTION_STATUSES)[number];
export const PRODUCTION_STATUS_LABELS: Record<ProductionStatus, string> = { NONE: 'Not sent', SENT: 'Sent to production', PLANNED: 'Planned', IN_PROGRESS: 'In production', COMPLETED: 'Production complete' };

// ---------- Quotation & sales order ----------

/**
 * A quotation / order line (owner, 2026-11-02): no discount and no VAT per line — the VAT comes from the customer's
 * VAT scenario for the whole document. A rate of 0 is "rate pending" (set later) unless the line is free of charge.
 */
export const salesLineSchema = z.object({
  id: z.string().uuid().optional(),
  itemId: uuid('Select an item'),
  description: opt(300),
  qty: qty(),
  rate: rate(),
  /** Given away at no charge: a 0 rate that is final (not "rate pending"). */
  isFree: z.boolean().optional().default(false),
  /** Not entered any more; anything but 0 is refused so a discount is never silently dropped. */
  discountPct: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number()).refine((v) => !v, 'Sales orders and quotations have no discount; enter the agreed price as the rate').optional(),
  /** Ignored: the VAT code comes from the document's VAT scenario. */
  taxCodeId: z.string().uuid().nullable().optional(),
  /** Quotation line an order line came from. */
  sourceLineId: uuidOpt,
  notes: opt(300),
}).refine((l) => !l.isFree || !l.rate, { path: ['rate'], message: 'A free-of-charge line has no rate; untick "Free" to charge it', ...always });

/** A line still to be priced: rate 0 and not free of charge. */
export const isRatePending = (l: { rate: number; isFree?: boolean }) => !l.isFree && Math.abs(l.rate) < 0.00005;

export const SALES_SOURCES = ['WEBSITE', 'SOCIAL_MEDIA', 'OFFLINE'] as const;
export type SalesSource = (typeof SALES_SOURCES)[number];
export const SALES_SOURCE_LABELS: Record<SalesSource, string> = { WEBSITE: 'Website', SOCIAL_MEDIA: 'Social media', OFFLINE: 'Offline (showroom, phone, visit)' };

const customerBlock = {
  customerId: uuid('Select the customer'),
  /** Buyer name / phone printed on the document (old-system cash sales carry them). */
  walkInName: opt(120),
  walkInPhone: opt(30),
  salespersonId: uuid('Select the salesperson'),
  paymentTermId: uuid('Select payment terms'),
  /** EXCLUSIVE / INCLUSIVE for 5% scenarios; zero-rated scenarios ignore it. */
  vatMode: z.enum(VAT_MODES),
  /** Empty = the customer's VAT scenario. Another one needs "Change VAT rate" and a reason. */
  vatScenario: z.enum(VAT_SCENARIOS).nullable().optional().transform((v) => v ?? null),
  vatScenarioReason: opt(300),
  reference: opt(60),
  shipTo: opt(400),
  notes: opt(1000),
};

export const quotationSchema = z
  .object({ docDate: isoDate, validUntil: optDate, salesSource: z.enum(SALES_SOURCES, { error: 'Select where the sale came from' }), ...customerBlock, lines: z.array(salesLineSchema).min(1, 'Add at least one item').max(300) })
  .refine((v) => !v.validUntil || v.validUntil >= v.docDate, { path: ['validUntil'], message: 'Cannot be before the quotation date', ...always });
export type QuotationInput = z.infer<typeof quotationSchema>;

export const salesOrderSchema = z
  .object({
    docDate: isoDate,
    /** Delivery date the customer asked for (the committed date comes from production). */
    deliveryDate: optDate,
    /** When the customer is expected to pay (optional). */
    paymentDate: optDate,
    customerPo: opt(60),
    quotationId: uuidOpt,
    salesSource: z.enum(SALES_SOURCES, { error: 'Select where the sale came from' }),
    ...customerBlock,
    lines: z.array(salesLineSchema).min(1, 'Add at least one item').max(300),
  })
  .refine((v) => !v.deliveryDate || v.deliveryDate >= v.docDate, { path: ['deliveryDate'], message: 'Cannot be before the order date', ...always })
  .refine((v) => !v.paymentDate || v.paymentDate >= v.docDate, { path: ['paymentDate'], message: 'Cannot be before the order date', ...always });
export type SalesOrderInput = z.infer<typeof salesOrderSchema>;

// ---------- Delivery note ----------

export const deliveryLineSchema = z.object({ id: z.string().uuid().optional(), soLineId: uuid('Pick the order line'), qty: qty(), notes: opt(300) });
export const deliverySchema = z.object({
  docDate: isoDate,
  orderId: uuid('Select the sales order'),
  warehouseId: uuid('Select the warehouse'),
  vehicleNo: opt(30),
  driverName: opt(80),
  shipTo: opt(400),
  notes: opt(500),
  lines: z.array(deliveryLineSchema).min(1, 'Add at least one line').max(300),
});
export type DeliveryInput = z.infer<typeof deliverySchema>;

// ---------- Tax invoice ----------

/** Invoice lines bill delivered goods (from the delivery line) or a service / non-stock item ordered on the SO. */
export const invoiceLineSchema = z.object({ id: z.string().uuid().optional(), sourceLineId: uuid('Pick the delivery or order line'), qty: qty(), notes: opt(300) });
export const invoiceSchema = z.object({
  docDate: isoDate,
  orderId: uuid('Select the sales order'),
  deliveryId: uuidOpt,
  dueDate: optDate,
  notes: opt(500),
  lines: z.array(invoiceLineSchema).min(1, 'Add at least one line').max(300),
});
export type InvoiceInput = z.infer<typeof invoiceSchema>;

/** Dispatch & invoice in one step: the delivery note and its invoice are posted together. */
export const dispatchInvoiceSchema = z.object({
  docDate: isoDate,
  orderId: uuid('Select the sales order'),
  warehouseId: uuid('Select the warehouse'),
  vehicleNo: opt(30),
  driverName: opt(80),
  notes: opt(500),
  lines: z.array(deliveryLineSchema).min(1, 'Add at least one line').max(300),
});
export type DispatchInvoiceInput = z.infer<typeof dispatchInvoiceSchema>;

// ---------- Credit note ----------

export const creditLineSchema = z.object({ id: z.string().uuid().optional(), invoiceLineId: uuid('Pick the invoice line'), qty: qty(), rate: rate().optional(), notes: opt(300) });
export const creditNoteSchema = z
  .object({
    docDate: isoDate,
    invoiceId: uuid('Select the invoice'),
    /** Goods come back into stock (returns) or not (price allowance, damaged goods written off). */
    returnStock: z.boolean(),
    warehouseId: uuidOpt,
    reason: req('Reason', 300),
    lines: z.array(creditLineSchema).min(1, 'Add at least one line').max(300),
  })
  .refine((v) => !v.returnStock || !!v.warehouseId, { path: ['warehouseId'], message: 'Select the warehouse the goods come back to', ...always });
export type CreditNoteInput = z.infer<typeof creditNoteSchema>;

// ---------- Responses ----------

export const SALES_DOC_KINDS = { quotations: 'sales.quotation', orders: 'sales.order', deliveries: 'sales.delivery', invoices: 'sales.invoice', credits: 'sales.credit_note' } as const;
export type SalesDocKind = keyof typeof SALES_DOC_KINDS;
export type SalesDocType = (typeof SALES_DOC_KINDS)[SalesDocKind];

export interface SalesDocLine {
  id: string;
  lineNo: number;
  item: { id: string; code: string; name: string; uom: string; decimals: number; isStockTracked: boolean } | null;
  description: string;
  qty: number;
  rate: number;
  discountPct: number;
  taxCodeId: string | null;
  taxRate: number;
  net: number;
  tax: number;
  total: number;
  sourceLineId: string | null;
  sourceNumber: string | null;
  isFree: boolean;
  /** Quotations / orders: rate 0 and not free — to be set before invoicing. */
  ratePending: boolean;
  /** Orders: production part of the line. */
  part?: ProductionPart;
  /** Orders: quantity planned in production (furniture jobs / mattress production orders). */
  planned?: number;
  /** Orders: delivered / invoiced; deliveries: invoiced; invoices: credited. */
  delivered?: number;
  invoiced?: number;
  credited?: number;
  notes: string;
}

export interface SalesDocDetail {
  id: string;
  docType: SalesDocType;
  number: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  customer: { id: string; code: string; name: string; trn: string; customerType: import('./masters').CustomerType | null; vatScenario: VatScenario | null } | null;
  /** VAT scenario of the document (quotations / orders; deliveries and invoices take the order's). Null on older documents. */
  vatScenario: VatScenario | null;
  walkInName: string;
  walkInPhone: string;
  salespersonId: string | null;
  paymentTermId: string | null;
  warehouseId: string | null;
  vatMode: VatMode;
  sourceId: string | null;
  sourceNumber: string | null;
  orderId: string | null;
  orderNumber: string | null;
  salesSource: SalesSource | null;
  fields: { validUntil: string | null; deliveryDate: string | null; paymentDate: string | null; committedDate: string | null; completionDate: string | null; dueDate: string | null; customerPo: string; reference: string; shipTo: string; vehicleNo: string; driverName: string; reason: string; notes: string; returnStock: boolean };
  productionStatus: ProductionStatus;
  /** Orders: lines by production part (furniture → planning, mattress → mattress production, stock / services not made). */
  parts: { part: ProductionPart; lines: number; qty: number; planned: number }[];
  /** Quotations / orders with a line still at rate 0 (not free). */
  pricesPending: boolean;
  /** Approved orders: why it can no longer be edited (null = editable by someone with "Edit approved orders"). */
  editLock: string | null;
  /** What a printed document shows beyond the fields above. */
  /** Invoices: how much is paid (receipts / credit notes matched to it) — null for other documents. */
  payment: InvoicePayment | null;
  printInfo: { address: string; city: string; placeOfSupply: string | null; salesperson: string | null; paymentTerm: string | null; deliveryNumbers: string[]; deliveryDate: string | null; salesSource: string | null; vatScenario: VatScenario | null };
  lines: SalesDocLine[];
  net: number;
  tax: number;
  total: number;
  /** Orders: delivery / invoicing progress in %. */
  progress: { delivered: number; invoiced: number } | null;
  related: { id: string; docType: string; number: string | null; status: import('./documents').DocStatus; docDate: string; total: number }[];
  migrated: boolean;
  legacyNumber: string | null;
  createdBy: string | null;
  createdAt: string;
  version: number;
  actions: import('./documents').WorkflowAction[];
}

export interface SalesDocListItem {
  id: string;
  docType: string;
  number: string | null;
  legacyNumber: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  customer: string | null;
  salesperson: string | null;
  reference: string;
  total: number;
  productionStatus: ProductionStatus | null;
  progress: { delivered: number; invoiced: number } | null;
  payment: InvoicePayment | null;
  /** Orders: payment expected on. */
  paymentDate: string | null;
  /** Quotations / orders: a line is still at rate 0. */
  pricesPending: boolean;
  migrated: boolean;
}

export type InvoicePaymentStatus = 'UNPAID' | 'PARTLY_PAID' | 'PAID';
export interface InvoicePayment {
  status: InvoicePaymentStatus;
  amount: number;
  paid: number;
  open: number;
  /** Receipts / credit notes / adjustments that settled it. */
  settledBy?: { documentId: string; number: string | null; docType: string; docDate: string; amount: number }[];
}

export interface OpenSalesLine {
  lineId: string;
  documentId: string;
  number: string | null;
  item: SalesDocLine['item'];
  description: string;
  qty: number;
  done: number;
  open: number;
  rate: number;
  discountPct: number;
  taxCodeId: string | null;
  taxRate: number;
  isFree: boolean;
}

export const openQty = (q: number, done: number) => round4(q - done);

/** Set the rates of order lines still at 0 (after dispatch too); nothing else on the order changes. */
export const setPendingRatesSchema = z.object({
  version: z.number().int(),
  lines: z.array(z.object({ id: uuid('Pick the line'), rate: z.coerce.number({ error: 'Enter the rate' }).gt(0, 'Enter a rate above 0').max(1e9, 'Rate is too large') })).min(1, 'Enter at least one rate').max(300),
});
export type SetPendingRatesInput = z.infer<typeof setPendingRatesSchema>;

/** Zero-rated invoice still without its export / exit proof (warn only; 5% is due if none within 90 days). */
export interface ExportProofRow {
  id: string;
  number: string | null;
  docDate: string;
  customer: string;
  vatScenario: VatScenario | null;
  total: number;
  proofs: number;
  daysLeft: number;
}
